import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Phone, User, Clock, MapPin, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '@/services/api';

interface Customer {
  id: number | string;
  name: string;
  phone?: string;
  email?: string;
  total_bookings?: number;
  last_booking?: string;
  member_level?: string;
  recent_courts?: string[];
  source?: string;
}

interface QuickCustomerSearchProps {
  onSelectCustomer?: (customer: Customer) => void;
  placeholder?: string;
  className?: string;
}

const QuickCustomerSearch: React.FC<QuickCustomerSearchProps> = ({
  onSelectCustomer,
  placeholder,
  className = ""
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<Customer[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isSearching, setIsSearching] = useState(false);

  // 搜索逻辑
  useEffect(() => {
    const term = searchTerm.trim();
    if (term.length < 2) {
      setResults([]);
      setIsOpen(false);
      setSelectedIndex(-1);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      const currentTerm = searchTerm.trim();
      const result = await api.customers.search(currentTerm);
      if (currentTerm !== searchTerm.trim()) {
        setIsSearching(false);
        return;
      }

      if (result.error || !Array.isArray(result.data)) {
        setResults([]);
        setIsOpen(true);
        setSelectedIndex(-1);
      } else {
        const customers = (result.data as unknown[])
          .map((record) => {
            if (!record || typeof record !== 'object') return null;
            const item = record as Record<string, unknown>;
            const id = item.id;
            const name = item.name;
            if ((typeof id !== 'string' && typeof id !== 'number') || typeof name !== 'string') return null;

            const phone = item.phone;
            const email = item.email;
            const total_bookings = item.total_bookings;
            const last_booking = item.last_booking;
            const member_level = item.member_level;
            const recent_courts = item.recent_courts;
            const source = item.source;

            return {
              id,
              name,
              phone: typeof phone === 'string' ? phone : undefined,
              email: typeof email === 'string' ? email : undefined,
              total_bookings: typeof total_bookings === 'number' ? total_bookings : undefined,
              last_booking: typeof last_booking === 'string' ? last_booking : undefined,
              member_level: typeof member_level === 'string' ? member_level : undefined,
              recent_courts: Array.isArray(recent_courts)
                ? recent_courts.filter((court): court is string => typeof court === 'string')
                : undefined,
              source: typeof source === 'string' ? source : undefined,
            } as Customer;
          })
          .filter((item): item is Customer => Boolean(item));

        setResults(customers);
        setIsOpen(true);
        setSelectedIndex(-1);
      }
      setIsSearching(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // 键盘导航
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => prev < results.length - 1 ? prev + 1 : 0);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : results.length - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          handleSelectCustomer(results[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleSelectCustomer = (customer: Customer) => {
    setSearchTerm(customer.name);
    setIsOpen(false);
    onSelectCustomer?.(customer);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setResults([]);
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  const getMemberLevelColor = (level?: string) => {
    switch (level) {
      case 'VIP':
        return 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white';
      case '金卡':
        return 'bg-gradient-to-r from-yellow-300 to-yellow-400 text-yellow-900';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className={`relative ${className}`}>
      {/* 搜索输入框 */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || t('createBooking.searchPlaceholder')}
          className="input pl-12 pr-10 w-full"
          autoComplete="off"
        />
        
        {searchTerm && (
          <button
            onClick={clearSearch}
            className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* 搜索结果下拉列表 */}
      <AnimatePresence>
        {isOpen && results.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute z-50 w-full mt-2 bg-white rounded-xl shadow-hard border border-gray-200 max-h-80 overflow-y-auto"
          >
            <div className="p-2">
              <div className="text-xs text-gray-500 px-3 py-2 border-b border-gray-100">
                {t('createBooking.foundCustomers', { count: results.length })}
              </div>
              
              {results.map((customer, index) => (
                <motion.div
                  key={customer.id}
                  onClick={() => handleSelectCustomer(customer)}
                  className={`p-3 rounded-lg cursor-pointer transition-all ${
                    index === selectedIndex 
                      ? 'bg-primary-50 border border-primary-200' 
                      : 'hover:bg-gray-50'
                  }`}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-500" />
                          <span className="font-medium text-gray-900">
                            {customer.name}
                          </span>
                        </div>
                        
                        {customer.member_level && (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getMemberLevelColor(customer.member_level)}`}>
                            {customer.member_level}
                          </span>
                        )}
                      </div>
                      
                      <div className="space-y-1">
                        {customer.phone && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Phone className="h-3 w-3" />
                            <span>{customer.phone}</span>
                          </div>
                        )}
                        
                        {(customer.last_booking || customer.total_bookings !== undefined) && (
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            {customer.last_booking && (
                              <div className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                <span>{t('createBooking.lastBooking', { date: customer.last_booking })}</span>
                              </div>
                            )}
                            
                            {customer.total_bookings !== undefined && (
                              <span>{t('createBooking.totalBookings', { count: customer.total_bookings })}</span>
                            )}
                          </div>
                        )}
                        
                        {customer.recent_courts && customer.recent_courts.length > 0 && (
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <MapPin className="h-3 w-3" />
                            <span>{t('createBooking.frequentCourts', { courts: customer.recent_courts.join(', ') })}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 无结果提示 */}
      <AnimatePresence>
        {isOpen && searchTerm.length >= 2 && results.length === 0 && !isSearching && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute z-50 w-full mt-2 bg-white rounded-xl shadow-medium border border-gray-200 p-4"
          >
            <div className="text-center text-gray-500">
              <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">{t('createBooking.noCustomersFound')}</p>
              <p className="text-xs mt-1">{t('createBooking.searchHint')}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default QuickCustomerSearch;
